import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND, TESTIDS, SQUARE_BOOKING_URL } from "@/lib/site";

const navLinks = [
  { to: "/", label: "Home", key: "home" },
  { to: "/about", label: "About", key: "about" },
  { to: "/services", label: "Services", key: "services" },
];

const footerLinks = [
  ...navLinks,
  { to: "/faq", label: "FAQ", key: "faq" },
  { to: "/policies", label: "Policies", key: "policies" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col grain">
      <header
        data-testid={TESTIDS.nav.root}
        className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0C]/60 backdrop-blur-xl border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="font-display font-bold text-lg md:text-xl tracking-tight uppercase text-white">
            {BRAND.name}<span className="text-white/40">.</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={TESTIDS.nav[l.key]}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.2em] font-semibold transition-colors ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <a
            href={SQUARE_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={TESTIDS.nav.ctaDesktop}
            className="hidden lg:inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold hover:bg-zinc-200 transition-colors group"
          >
            Book <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <button
            data-testid={TESTIDS.nav.mobileToggle}
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div
            data-testid={TESTIDS.nav.mobileMenu}
            className="lg:hidden border-t border-white/10 bg-[#0A0A0C]"
          >
            <nav className="flex flex-col px-6 py-6 gap-5">
              {navLinks.map((l) => (
                <NavLink
                  key={l.key}
                  to={l.to}
                  data-testid={`${TESTIDS.nav[l.key]}-mobile`}
                  className={({ isActive }) =>
                    `text-sm uppercase tracking-[0.2em] font-semibold ${
                      isActive ? "text-white" : "text-zinc-400"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              <a
                href={SQUARE_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 bg-white text-black px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold text-center"
              >
                Book a Consultation
              </a>
            </nav>
          </div>
        )}
      </header>

      <main className="flex-1 pt-16 md:pt-20 relative z-10">{children}</main>

      <footer
        data-testid={TESTIDS.footer.root}
        className="relative z-10 border-t border-white/10 bg-[#0A0A0C]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-8">
            <p className="label-eyebrow mb-6">Ready when you are</p>
            <h2 className="font-display uppercase font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white">
              Move Better.<br />
              <span className="text-white/50">Build a Stronger</span> Foundation
            </h2>
            <p className="mt-8 max-w-xl text-zinc-400 leading-relaxed">
              Book a consultation today. We'll map out where your body is, where you want it to go, and build a plan that gets you there.
            </p>
            <a
              href={SQUARE_BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={TESTIDS.footer.cta}
              className="mt-10 inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-bold hover:bg-zinc-200 transition-colors group"
            >
              Book a Consultation
              <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </div>
          <div className="md:col-span-4 flex flex-col gap-8">
            <div>
              <p className="label-eyebrow mb-3">Contact</p>
              <a
                href={`tel:${BRAND.phone}`}
                data-testid={TESTIDS.footer.phone}
                className="block font-display uppercase text-2xl font-bold text-white hover:text-white/70 transition-colors"
              >
                {BRAND.phoneDisplay}
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email"
                className="mt-2 block text-sm text-zinc-400 hover:text-white transition-colors break-all"
              >
                {BRAND.email}
              </a>
              <p className="mt-2 text-sm text-zinc-500">{BRAND.serviceArea}</p>
            </div>
            <div>
              <p className="label-eyebrow mb-3">Explore</p>
              <ul className="space-y-2">
                {footerLinks.map((l) => (
                  <li key={l.key}>
                    <Link to={l.to} className="text-sm text-zinc-400 hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <p className="text-xs text-zinc-500 uppercase tracking-[0.2em]">
              © {new Date().getFullYear()} {BRAND.fullName}
            </p>
            <p className="text-xs text-zinc-500 uppercase tracking-[0.2em]">
              Personal Training · Fascial Stretch Therapy · Mobility
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
